import AppButton from '@nxsys/components/AppButton';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SettingForm, {
  SettingFormRef,
  SettingValues,
} from './components/SettingForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const SettingTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;
  const formRef = useRef<SettingFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: SettingValues) => {
      setIsUpdating(true);
      try {
        let params: UpdateCompanyByIdParams = {};

        if (!isBureau) {
          params = {
            isSendEmailSelfBill: values.isSendEmailSelfBill,
            isSendEmailDraftSelfBill: values.isSendEmailDraftSelfBill,
            isSendEmailP45: values.isSendEmailP45,
            isSendEmailP60: values.isSendEmailP60,
            sendEmailP60At: values.sendEmailP60At?.toISOString() || null,
            showHPEntitlement: values.showHPEntitlement,
            showHPEmployerCostsYTD: values.showHPEmployerCostsYTD,
            verifyPayslip: values.verifyPayslip,
          };
        } else if (isUK) {
          params = {
            isSendEmailSelfBill: values.isSendEmailSelfBill,
            isSendEmailDraftSelfBill: values.isSendEmailDraftSelfBill,
            isSendEmailP45: values.isSendEmailP45,
            isSendEmailP60: values.isSendEmailP60,
            sendEmailP60At: values.sendEmailP60At?.toISOString() || null,
            showHPEntitlement: values.showHPEntitlement,
            verifyPayslip: values.verifyPayslip,
          };
        } else if (isIreland) {
          params = {
            isSendEmailSelfBill: values.isSendEmailSelfBill,
            isSendEmailDraftSelfBill: values.isSendEmailDraftSelfBill,
            showHPEntitlement: values.showHPEntitlement,
            verifyPayslip: values.verifyPayslip,
          };
        }

        await updateData(params);
      } finally {
        setIsUpdating(false);
      }
    },
    [updateData, isBureau, isUK, isIreland],
  );

  return (
    <div>
      {data && (
        <>
        <SettingForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            isSendEmailSelfBill: data.isSendEmailSelfBill ?? false,
            isSendEmailDraftSelfBill: data.isSendEmailDraftSelfBill ?? false,
            isSendEmailP45: data.isSendEmailP45 ?? false,
            isSendEmailP60: data.isSendEmailP60 ?? false,
            sendEmailP60At: data.sendEmailP60At
              ? moment(data.sendEmailP60At)
              : null,
            showHPEntitlement: data.showHPEntitlement ?? false,
            showHPEmployerCostsYTD: data.showHPEmployerCostsYTD ?? false,
            verifyPayslip: data.verifyPayslip ?? false,
          }}
        />
        <div className="flex items-center justify-start gap-3 pt-4">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle
                color={defaultTheme.theme.palette.white}
              />
            }
            onClick={() => formRef.current?.submit()}
            loading={isUpdating}
          />
        </div>
        </>
      )}
    </div>
  );
};

export default SettingTab;
